<template>
    <AppLayout>
        <div class="max-w-7xl mx-auto px-4 py-6">
            <!-- Page Title -->
            <div class="mb-6">
                <h1 class="text-2xl md:text-3xl font-bold text-gray-800 dark:text-white">
                    {{ $tc('LBL_ORDER', 2) }}
                </h1>
                <!-- Breadcrumb -->
                <nav
                    class="text-sm text-gray-500 mb-6"
                    aria-label="Breadcrumb"
                >
                    <ol class="flex items-center space-x-2">
                        <li class="flex items-center">
                            <inertia-link
                                :href="route('landing')"
                                class="text-green-600 hover:underline"
                            >
                                {{ $t('LBL_HOME') }}
                            </inertia-link>
                            <svg
                                class="w-3 h-3 mx-2 text-green-400"
                                fill="currentColor"
                                viewBox="0 0 320 512"
                            >
                                <path
                                    d="M285.476 272.971L91.132 467.314c-9.373 9.373-24.569 9.373-33.941 0l-22.667-22.667c-9.357-9.357-9.375-24.522-.04-33.901L188.505 256 34.484 101.255c-9.335-9.379-9.317-24.544.04-33.901l22.667-22.667c9.373-9.373 24.569-9.373 33.941 0L285.475 239.03c9.373 9.372 9.373 24.568.001 33.941z"
                                />
                            </svg>
                        </li>
                        <li class="flex items-center">
                            <inertia-link
                                :href="route('bills.placed')"
                                class="text-green-600 hover:underline"
                            >
                                {{ $t('LBL_INVOICE') }}
                            </inertia-link>
                            <svg
                                class="w-3 h-3 mx-2 text-green-400"
                                fill="currentColor"
                                viewBox="0 0 320 512"
                            >
                                <path
                                    d="M285.476 272.971L91.132 467.314c-9.373 9.373-24.569 9.373-33.941 0l-22.667-22.667c-9.357-9.357-9.375-24.522-.04-33.901L188.505 256 34.484 101.255c-9.335-9.379-9.317-24.544.04-33.901l22.667-22.667c9.373-9.373 24.569-9.373 33.941 0L285.475 239.03c9.373 9.372 9.373 24.568.001 33.941z"
                                />
                            </svg>
                        </li>
                        <li class="text-gray-700">
                            {{ $tc('LBL_NEW_ORDER', 1) }}
                        </li>
                    </ol>
                </nav>
            </div>
            <!-- breadcrumb end -->

            <div class="w-full my-3 text-xs bg-white rounded shadow-sm md:text-sm">
                <div class="flex flex-col md:items-center md:flex-row md:justify-between">
                    <div class="flex flex-col p-2">
                        <p class="font-semibold">
                            Order <span class="text-green-600">#{{ bill.bill_number }} </span>
                        </p>

                        <div
                            v-if="bill.wholesaler || bill.user"
                            class="flex"
                        >
                            <span class="mr-1 text-gray-400">
                                {{ $page.props.roles[0] === 'retailer' ? 'From' : 'By' }}</span
                            >
                            <p class="font-semibold">
                                {{ $page.props.roles[0] === 'retailer' ? bill.wholesaler.name : bill.user.name }}
                            </p>
                        </div>

                        <p class="text-xs text-gray-400">Placed on {{ bill.created_at | readableDate }}</p>
                    </div>

                    <!-- Product Status Tracker  -->
                    <ProductTracker
                        v-if="isRetailer"
                        :status="bill.delivery_status"
                    />

                    <div class="flex items-center gap-4 p-2">
                        <div
                            v-if="!isRetailer"
                            class="px-4 py-2 bg-green-300 rounded opacity-50 font-semibold"
                        >
                            {{ $t('LBL_ORDER_STATUS') }}: {{ bill.delivery_status }}
                            <!-- <t-select
                                :value="bill.delivery_status"
                                :options="deliveryStatusList"
                                disabled
                                @change="updateDeliveryStatus(bill, $event)"
                            /> -->
                            <!-- :disabled="isBillPaid(bill) || isBillPartial(bill)" -->
                        </div>

                        <div class="flex items-center gap-4">
                            <!-- <t-button
                                type="button"
                                @click="togglePaymentListModal(bill)"
                            >
                                Payment List
                            </t-button> -->

                            <div
                                v-if="isBillPaid(bill) || bill.total_amount == 0"
                                class="flex items-center gap-2"
                            >
                                <div class="px-2 py-1 text-white bg-green-400 rounded">
                                    {{
                                        hasPendingUserPayments(bill)
                                            ? 'Declared'
                                            : bill.total_amount == 0
                                            ? 'Free'
                                            : 'Paid'
                                    }}
                                </div>
                                <button
                                    class="p-1 rounded-full cursor-pointer hover:bg-gray-200"
                                    title="Print Invoice"
                                    @click="printInvoice(bill)"
                                >
                                    <PrintIcon class="w-5" />
                                </button>
                            </div>

                            <div
                                v-if="isBillPending(bill) && bill.total_amount != 0"
                                class="flex items-center gap-2"
                            >
                                <div class="px-4 py-2 bg-blue-300 rounded opacity-50">
                                    {{ $t('LBL_PAYMENT_STATUS') }}:
                                    {{
                                        hasPendingUserPayments(bill)
                                            ? `Declared (${hasPendingUserPayments(bill)})`
                                            : 'Pending'
                                    }}
                                </div>
                                <button
                                    v-if="
                                        isProductsPacked(bill) || isProductsShipped(bill) || isProductsDelivered(bill)
                                    "
                                    class="p-1 rounded-full cursor-pointer hover:bg-gray-200"
                                    title="Print Invoice"
                                    @click="printInvoice(bill)"
                                >
                                    <ViewIcon
                                        v-if="isRetailer"
                                        class="w-5 mr-2 fill-current"
                                    />
                                    <PrintIcon
                                        v-else
                                        class="w-5"
                                    />
                                </button>
                            </div>

                            <div
                                v-if="isBillPartial(bill) && bill.total_amount !== 0"
                                class="px-4 py-2 bg-yellow-300 rounded opacity-50"
                            >
                                Partial
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Order Details Wrapper -->
                <div class="flex flex-col md:flex-row gap-6">
                    <!-- Product List -->
                    <div class="flex-1">
                        <div
                            v-for="order in bill.transactions"
                            :key="order.id"
                            class="flex flex-col md:flex-row gap-4 p-4 mb-4 bg-white border rounded shadow-sm"
                        >
                            <img
                                :src="
                                    order.product.images.length ? order.product.images[0].image_path : common.defaultImg
                                "
                                class="w-full md:w-32 h-32 object-cover rounded cursor-pointer"
                                @click="gotoProduct(order)"
                            />
                            <div class="flex-grow">
                                <h3
                                    class="text-base font-semibold text-gray-800 cursor-pointer line-clamp-2"
                                    @click="gotoProduct(order)"
                                >
                                    {{ order.product.name }}
                                </h3>
                                <p class="text-sm text-gray-500 mb-2">{{ order.product.brand.name }}</p>
                                <div class="text-sm text-gray-600">
                                    <p>Qty: {{ order.qty }}</p>
                                    <p>
                                        Price: {{ config(common.currency, order.product.owner) }}
                                        {{ parseFloat(order.product.price).toFixed(2) }}
                                    </p>
                                    <p v-if="order.vat != 0">
                                        VAT: {{ config(common.currency, order.product.owner) }} {{ order.vat }}
                                    </p>
                                </div>
                                <div class="mt-2 text-green-600 font-semibold">
                                    Total: {{ config(common.currency, order.product.owner) }}
                                    {{ parseFloat(order.grand_total).toFixed(2) }}
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Sticky Summary Section -->
                    <div class="md:w-1/3 h-fit sticky top-4">
                        <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                            <h2 class="text-lg font-semibold mb-4 text-gray-800">Order Summary</h2>
                            <div class="text-sm space-y-2">
                                <div class="flex justify-between">
                                    <span class="text-gray-600">Payment Method</span>
                                    <span class="font-semibold">{{ bill.payment_method }}</span>
                                </div>
                                <div class="flex justify-between">
                                    <span class="text-gray-600">Subtotal</span>
                                    <span class="font-semibold"
                                        >{{ config(common.currency) }} {{ subtotalWithVat }}</span
                                    >
                                </div>
                                <div class="flex justify-between">
                                    <span class="text-gray-600">Total Discount</span>
                                    <span class="text-red-500 font-semibold"
                                        >- {{ config(common.currency) }} {{ totalDiscountAmount }}</span
                                    >
                                </div>
                                <hr />
                                <div class="flex justify-between text-lg font-semibold text-green-600">
                                    <span>Grand Total</span>
                                    <span
                                        >{{ config(common.currency) }} {{ subtotalWithVat - totalDiscountAmount }}</span
                                    >
                                </div>
                            </div>

                            <!-- Notes -->
                            <div class="mt-6">
                                <h3 class="font-semibold text-sm text-gray-700 mb-1">Notes</h3>
                                <p class="text-gray-600 text-sm">{{ bill.notes || '--' }}</p>
                            </div>

                            <!-- Token Button -->
                            <div
                                v-if="isWholesaler && bill.delivery_status === 'Placed'"
                                class="mt-6"
                            >
                                <t-button
                                    variant="success"
                                    @click="generateToken"
                                    class="w-full py-2"
                                >
                                    {{ $t('LBL_GENERATE_TOKEN_BTN') }}
                                </t-button>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- <div v-for="order in bill.transactions" :key="order.id"
                    class="flex flex-col gap-2 p-5 md:flex-row md:gap-4">
                    <div class="flex flex-col mb-3 md:flex-row md:gap-3 md:w-2/5">
                        <div class="flex-shrink-0 w-full md:w-1/3">
                            <img :src="order.product.images.length > 0
                                    ? order.product.images[0].image_path
                                    : common.defaultImg
                                " alt="Product Image" class="w-full h-32 cursor-pointer md:h-28" @click="
                                    goto('products.show', {
                                        dummy: `${convertToKebabCase(order.product.brand.name)}-${convertToKebabCase(
                                            order.product.name,
                                        )}`,
                                        product: order.product.slug,
                                    })
                                    ">
                        </div>
                        <div class="mt-1 md:flex-grow">
                            <div class="mb-4 font-semibold cursor-pointer line-clamp-3" @click="
                                goto('products.show', {
                                    dummy: `${convertToKebabCase(order.product.brand.name)}-${convertToKebabCase(
                                        order.product.name,
                                    )}`,
                                    product: order.product.slug,
                                })
                                ">
                                {{ order.product.name }}
                            </div>
                            <div class="flex text-gray-500">
                                <img v-if="order.product.brand.images.length"
                                    :src="order.product.brand.images[0].image_path" class="h-5 mr-2">
                                {{ order.product.brand.name }}
                            </div>
                        </div>
                    </div>
                    <div class="md:w-1/5">
                        <div>Qty: {{ order.qty }}</div>
                        <div>
                            Price/Piece: {{ config(common.currency, order.product.owner) }}
                            {{ parseFloat(order.product.price).toFixed(2) }}
                        </div>
                    </div>
                    <div class="flex-col justify-center px-4 rounded md:w-2/5 md:flex bg-gray-50">
                        <div class="flex justify-between">
                            <p>Subtotal ({{ order.qty }} item)</p>
                            <p>
                                {{ config(common.currency, order.product.owner) }}
                                {{ (order.qty * order.product.price).toFixed(2) }}
                            </p>
                        </div>
                        <div v-if="order.vat != 0" class="flex justify-between">
                            <p>Vat</p>
                            <p>+ {{ config(common.currency, order.product.owner) }} {{ order.vat }}</p>
                        </div> -->
                <!-- <div class="flex justify-between">
                            <p>Discount</p>
                            <p class="text-red-500">
                                - {{ config(common.currency, order.product.owner) }}
                                {{ parseFloat(order.discount).toFixed(2) }}
                            </p>
                        </div> -->
                <!-- <hr class="my-1">
                        <div class="flex justify-between mt-1 ">
                            <p>Total</p>
                            <p class="text-green-500 ">
                                {{ config(common.currency, order.product.owner) }}
                                {{ parseFloat(order.grand_total).toFixed(2) }}
                            </p>
                        </div>
                    </div>
                </div>

                <div class="flex items-center justify-between p-5">
                    <div class="flex flex-grow pr-6 mt-1 text-md md:w-2/5">
                        <p class="mr-2">
                            Notes:
                        </p>
                        <p class="font-semibold">
                            {{ bill.notes ? bill.notes : '--' }}
                        </p>
                    </div>
                    <div class="pl-4 md:w-2/5">
                        <div class="flex justify-between mt-1 font-semibold">
                            <p>Payment Method</p>
                            <p>
                                {{ bill.payment_method }}
                            </p>
                        </div>
                        <div class="flex justify-between mt-1 font-semibold">
                            <p>Subtotal</p>
                            <p>{{ config(common.currency) }} {{ subtotalWithVat }}</p>
                        </div>
                        <div class="flex justify-between mt-1 font-semibold">
                            <p>Total Discount</p>
                            <p class="text-red-500">
                                <span class="mr-1 ">-</span>{{ config(common.currency) }}
                                {{ totalDiscountAmount }}
                            </p>
                        </div>
                        <hr class="my-1">
                        <div class="flex justify-between mt-1 text-lg font-semibold">
                            <p>Grand total</p>
                            <p class="text-green-500 ">
                                {{ config(common.currency) }} {{ subtotalWithVat - totalDiscountAmount }}
                            </p>
                        </div>
                    </div>
                </div> -->
            </div>
        </div>

        <!-- <div v-if="isWholesaler && bill.delivery_status === 'Placed'" class="flex justify-end pb-6">
            <t-button type="button" variant="success" @click="generateToken">
                {{ $t('LBL_GENERATE_TOKEN_BTN') }}
            </t-button>
        </div> -->

        <InvoiceToken
            v-show="false"
            ref="invoiceToken"
            :bill="bill"
        />

        <t-modal
            v-if="currentBill"
            v-model="paymentListModal"
            header="List of payments made by the retailer."
        >
            <div v-if="currentBill.user_payments.length">
                <t-table
                    :headers="['Amount', 'Receipt', 'Date', 'Action']"
                    :data="currentBill.user_payments"
                    class="text-xs md:text-base"
                >
                    <template
                        slot="row"
                        slot-scope="{ trClass, tdClass, row }"
                    >
                        <tr :class="[trClass, 'text-xs md:text-base']">
                            <td :class="[tdClass]">
                                {{ config(common.currency) }} {{ parseFloat(row.amount).toFixed(2) }}
                            </td>
                            <td :class="[tdClass]">
                                <div
                                    class="text-blue-500 cursor-pointer hover:text-blue-700"
                                    @click="openReceiptInNewTab(row.receipt)"
                                >
                                    Open Receipt
                                </div>
                            </td>
                            <td :class="[tdClass]">
                                {{ row.created_at | fromNow }}
                            </td>
                            <td :class="[tdClass]">
                                <div
                                    v-if="!row.accepted_at && !row.deleted_at && isWholesaler"
                                    class="flex gap-2"
                                >
                                    <div
                                        class="px-2 py-1 text-xs text-white bg-green-400 rounded cursor-pointer md:text-sm"
                                        @click="paymentConfirm(row.bill_id, row.id)"
                                    >
                                        Confirm
                                    </div>
                                    <div
                                        class="px-2 py-1 text-xs text-white bg-red-400 rounded cursor-pointer md:text-sm"
                                        @click="paymentReject(row.bill_id, row.id)"
                                    >
                                        Reject
                                    </div>
                                </div>
                                <div v-else-if="!row.accepted_at && !row.deleted_at && !isWholesaler">Pending</div>
                                <div
                                    v-else-if="row.accepted_at && !row.deleted_at && !isWholesaler"
                                    class="inline-block px-2 py-1 text-sm text-white bg-green-400 rounded"
                                >
                                    Accepted
                                </div>
                                <div
                                    v-else-if="!row.accepted_at && row.deleted_at"
                                    class="inline-block px-2 py-1 text-sm text-white bg-red-400 rounded"
                                >
                                    Rejected
                                </div>
                            </td>
                        </tr>
                    </template>
                </t-table>
            </div>
            <div v-else>No payment declaration made yet.</div>
        </t-modal>
    </AppLayout>
</template>

<script>
    import axios from 'axios';
    import common from '@/common';
    import AppLayout from '@/Layouts/AppLayout.vue';
    import PrintIcon from '@/Components/Icons/PrintIcon.vue';
    import ProductTracker from '@/Components/ProductTracker.vue';
    import InvoiceToken from '@/Components/Invoice/InvoiceToken.vue';

    export default {
        name: 'PurchaseHistoryShow',

        components: {
            AppLayout,
            PrintIcon,
            ProductTracker,
            InvoiceToken,
        },

        props: {
            bill: {
                type: Object,
                required: true,
            },
        },

        data() {
            return {
                common,
                deliveryStatusList: ['Placed', 'Packed', 'Shipped', 'Delivered'],
                paymentListModal: false,
                currentBill: null,
            };
        },

        computed: {
            subtotalWithVat() {
                return this.bill.transactions
                    .reduce((acc, transaction) => {
                        const totalWithVat = parseFloat(transaction.total) + parseFloat(transaction.vat);
                        return acc + totalWithVat;
                    }, 0)
                    .toFixed(2);
            },

            totalDiscountAmount() {
                return this.bill.transactions
                    .reduce((acc, transaction) => acc + parseFloat(transaction.discount), 0)
                    .toFixed(2);
            },
        },

        methods: {
            isBillPaid(bill) {
                if (bill.payment_status === 'Paid') {
                    return true;
                }
                return false;
            },

            isBillPending(bill) {
                if (bill.payment_status === 'Pending') {
                    return true;
                }
                return false;
            },

            isProductsPacked(bill) {
                if (bill.delivery_status === 'Packed') {
                    return true;
                }
                return false;
            },

            isProductsShipped(bill) {
                if (bill.delivery_status === 'Shipped') {
                    return true;
                }
                return false;
            },

            isProductsDelivered(bill) {
                if (bill.delivery_status === 'Delivered') {
                    return true;
                }
                return false;
            },

            isBillPartial(bill) {
                if (bill.payment_status === 'Partial') {
                    return true;
                }
                return false;
            },

            updateDeliveryStatus(bill, value) {
                if (this.deliveryStatusList.includes(value) && !this.isBillPaid(bill)) {
                    const payload = {
                        delivery_status: value,
                        _method: 'PUT',
                    };
                    this.$dialog
                        .confirm({
                            title: 'Confirm',
                            text: `Are you sure you want to update the delivery status to ${value}?`,
                        })
                        .then((result) => {
                            if (result.isOk) {
                                this.$inertia.post(route('paymentStatus.update', bill.id), payload);
                            }
                        });
                }
            },

            totalDiscount() {
                return this.bill.transactions
                    .map((order) => order.discount)
                    .reduce((previousValue, currentValue) => parseInt(previousValue) + parseInt(currentValue), 0)
                    .toFixed(2);
            },

            togglePaymentListModal(bill = null) {
                if (bill !== null) {
                    this.currentBill = bill;
                    this.paymentListModal = true;
                } else {
                    this.currentBill = null;
                    this.paymentListModal = false;
                }
            },

            openReceiptInNewTab(receipt) {
                window.open(`/storage/${receipt}`);
            },

            printInvoice(bill) {
                this.newTab('invoice.print', { billId: bill.id });
            },

            paymentConfirm(billId, paymentId) {
                this.$inertia.get(route('paymentConfirm', { bill: billId, payment: paymentId }));
            },

            paymentReject(billId, paymentId) {
                this.$inertia.get(route('paymentReject', { bill: billId, payment: paymentId }));
            },

            hasPendingUserPayments(bill) {
                const pendingBills = bill.user_payments.filter(
                    (payment) => payment.accepted_at === null && payment.deleted_at === null,
                );
                return pendingBills.length;
            },

            printToken() {
                this.$refs.invoiceToken.printToken();
            },

            generateToken() {
                this.incrementTokenCountForBill()
                    .then(() => {
                        this.printToken();
                    })
                    .then(() => {
                        this.goto('bills.placed');
                    });
            },

            incrementTokenCountForBill() {
                return axios.post(route('tokenCount.increment', { bill_id: this.bill.id }));
            },
        },
    };
</script>
